/**
 * Phone normalization for Israeli mobile numbers.
 *
 * Accepts either:
 *   - local form     `05XXXXXXXX`   (10 digits, leading 0)
 *   - E.164 form     `+9725XXXXXXXX`
 * (separators ` `, `-`, `(`, `)` are tolerated and stripped)
 *
 * Always returns canonical E.164 `+9725XXXXXXXX`. Throws on anything else.
 *
 * Israeli mobile = national prefix `05` + 8 digits; the national significant
 * number after dropping the trunk `0` is `5XXXXXXXX` (9 digits).
 *
 * Lives in @slikerr/shared (framework-free) rather than in the api's auth
 * module because the mobile contact-import picker greys out un-importable
 * numbers with the SAME rule the server enforces — two implementations of
 * "what counts as a phone number" is an identity bug waiting to happen.
 * `apps/api/src/auth/phone.ts` re-exports this.
 */
export function normalizePhone(input: string): string {
  if (typeof input !== 'string') {
    throw new Error('Invalid phone number');
  }

  // Strip common separators only; keep digits and a possible leading '+'.
  const cleaned = input.trim().replace(/[\s\-().]/g, '');

  // Local: 05XXXXXXXX -> +9725XXXXXXXX
  const localMatch = /^0(5\d{8})$/.exec(cleaned);
  if (localMatch) {
    return `+972${localMatch[1]}`;
  }

  // E.164: +9725XXXXXXXX (already canonical)
  const e164Match = /^\+972(5\d{8})$/.exec(cleaned);
  if (e164Match) {
    return `+972${e164Match[1]}`;
  }

  throw new Error('Invalid phone number; expected Israeli mobile (05XXXXXXXX or +9725XXXXXXXX)');
}

/**
 * Non-throwing form, for UI that has to decide "can this row be ticked?" for
 * every contact in an address book at render time.
 */
export function isImportablePhone(input: string): boolean {
  try {
    normalizePhone(input);
    return true;
  } catch {
    return false;
  }
}

/**
 * The DISPLAY direction: a stored phone → what a person reads.
 * `+972501234567` → `050-1234567`.
 *
 * The counterpart of `normalizePhone` above, and deliberately its opposite in
 * temperament. `normalizePhone` is a gate — it throws, because storing a
 * malformed number is a real bug. This one is for rendering, so it must NEVER
 * throw and never hide a number it does not recognise: anything it cannot parse
 * comes back stripped-but-intact rather than blanked, because a admin looking at
 * a customer record needs to see whatever we actually hold, even when it is odd.
 *
 * Leaves a foreign number ALONE. `+14155552671` is not Israeli, has no leading
 * `0` form, and rewriting it would invent a number that does not exist — leads
 * in particular may legitimately carry one (see `admin-crm.router.ts`).
 *
 * KNOWN LIMIT, deliberate: only 10-digit numbers get the dash, which in practice
 * means mobiles (`05X` + 7). An Israeli landline is 9 digits (`03` + 7) and
 * comes back without a separator. Formatting it would need an area-code table
 * (2-digit `03`, but 3-digit `077`), and guessing wrong prints a number that
 * looks wrong to anyone who lives here. Left alone until something needs it.
 *
 * Lived in `apps/mobile/src/lib/phone.ts` until 2026-08-25, where the admin
 * console could not reach it — the web-clean rule aside, apps do not import from
 * each other. Moved here rather than copied, because two implementations of
 * "how do we write a phone number" drift into two different-looking consoles.
 */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  let local = raw.replace(/[\s-]/g, '');
  if (local.startsWith('+972')) local = '0' + local.slice(4);
  else if (local.startsWith('972')) local = '0' + local.slice(3);
  const digits = local.replace(/\D/g, '');
  if (digits.length === 10 && digits.startsWith('0')) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return local;
}
